import {
    useEffect,
    useMemo,
    useState,
    useContext,
    useCallback,
    useRef,
    KeyboardEvent
} from "react";

import { useBxpWebStargateClient } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client";
import { FastshiftSupportRequest } from "@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";
import { checkFeatureFlagGate } from "@wac-web/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@wac-web/utils/feature-flags/feature-flag-manifest";
import isEqual from "lodash/isEqual";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";

import Badge from "@atlaskit/badge";
import { IconButton } from "@atlaskit/button-next/new";
import Heading from "@atlaskit/heading";
import BookWithBookmarkIcon from "@atlaskit/icon/core/book-with-bookmark";
import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import ChevronRightIcon from "@atlaskit/icon/core/chevron-right";
import Link from "@atlaskit/link";
import Lozenge from "@atlaskit/lozenge-next";
import { Stack, Text, Inline, xcss, Box } from "@atlaskit/primitives-next";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import { USER_TYPE } from "@/common/constants";
import { PagePanelContext } from "@/common/contexts";
import {
    type TableHeads,
    type TableRows,
    type Pagination,
    type ControlsPayload,
    DEFAULT_CONTROLS_PAYLOAD
} from "@/common/fastshift-table";
import { useFetchSupportRequests, useUserInfo } from "@/common/hooks";
import { SAC_URL } from "@/common/routes/constants";
import { getOrgQueryParam } from "@/common/utils";
import { getStatusColor, getStatusText } from "@/common/utils";
import { useFastshiftSelector, useFastshiftDispatch } from "@/state";
import * as StateActions from "@/state/actions";
import { TABLE_IDS } from "@/state/constants";

import { messages } from "../messages";
import { MigrationRunbooksPanel } from "../migration-runbooks-panel";
import { MigrationTicketsPanel } from "../migration-tickets-panel";
import { RelatedTicketsExpandedRow } from "../migration-tickets-table";
import { ProductIcon } from "../product-icon";
import { PRODUCT_VARIANTS, ProductName } from "../types";
import { applyFilters, applySearch } from "../utils";
import { getMigrationPhaseDisplayText } from "../utils";

import { useMigrationTicketsUIAnalytics } from "./use-migration-tickets-ui-analytics";

const textEllipsisStyles = xcss({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const smallTextEllipsisStyles = xcss({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    font: token("font.body.small"),
    color: "color.text.subtle"
});

const stackInlineStyles = xcss({
    minWidth: "0"
});

const ticketBadgeStyles = xcss({
    marginInlineStart: "space.050"
});

const ticketCellStyles = xcss({
    minWidth: "0"
});

// Product name mapping with localization
const productNameMapping: Record<string, React.ReactNode> = {
    JIRA: (
        <FormattedMessage {...messages.migrationTicketsTableJiraProductName} />
    ),
    CONFLUENCE: (
        <FormattedMessage
            {...messages.migrationTicketsTableConfluenceProductName}
        />
    )
};

export const useMigrationTicketsTable = () => {
    const { openPanel, closePanel } = useContext(PagePanelContext);
    const runbooksPanelOpenCounter = useRef(0);
    const dispatch = useFastshiftDispatch();
    const stargateClient = useBxpWebStargateClient();
    const intl = useIntl();
    const {
        sendMigrationTicketsTableOpenSupportTicketClickUIEvent,
        sendRelatedTicketsSidePanelOpenSupportTicketClickUIEvent,
        sendRelatedTicketsSidePanelLinkClickUIEvent
    } = useMigrationTicketsUIAnalytics();

    const isRunbooksExperienceEnabled = checkFeatureFlagGate(
        featureFlags.sxpFastshiftPortalEnableMarathonRunbooks
    );

    const { data: user } = useUserInfo();
    const canViewRunbookActions =
        isRunbooksExperienceEnabled && user?.userType === USER_TYPE.INTERNAL;

    const [expandedTicketIds, setExpandedTicketIds] = useState<Set<string>>(
        new Set()
    );

    const toggleExpandedTicket = useCallback((supportTicketId: string) => {
        setExpandedTicketIds((previousIds) => {
            const nextIds = new Set(previousIds);
            if (nextIds.has(supportTicketId)) {
                nextIds.delete(supportTicketId);
            } else {
                nextIds.add(supportTicketId);
            }
            return nextIds;
        });
    }, []);

    const openPanelWithRelatedTickets = useCallback(
        (request: FastshiftSupportRequest) => {
            const content = (
                <MigrationTicketsPanel
                    supportTicketId={request.supportTicketId}
                    title={request.title}
                    fastShiftStatusReason={request.fastShiftStatusReason}
                    relatedTicketsCount={request.relatedTicketsCount}
                    relatedTickets={request.relatedTickets}
                    onClose={closePanel}
                />
            );

            openPanel(content);
        },
        [openPanel, closePanel]
    );

    const openPanelWithRunbooks = useCallback(
        (request: FastshiftSupportRequest) => {
            runbooksPanelOpenCounter.current += 1;
            const content = (
                <MigrationRunbooksPanel
                    key={`${request.supportTicketId}-${runbooksPanelOpenCounter.current}`}
                    supportTicketId={request.supportTicketId}
                    title={request.title}
                    onClose={closePanel}
                />
            );

            openPanel(content);
        },
        [openPanel, closePanel]
    );

    const {
        data: supportRequestsData,
        pending: supportRequestsPending,
        error: supportRequestsError
    } = useFastshiftSelector((state) => state.supportRequests);

    const { pending: customerDomainPending, data: customerDomainData } =
        useFastshiftSelector((state) => state.customerDomain);

    const selectedDomainOrg = useFastshiftSelector(
        (state) => state.selectedDomainOrg
    );

    const cachedTableControls = useFastshiftSelector(
        (state) => state.pageStateCache[TABLE_IDS.MIGRATION_TICKETS]
    );

    const [pagination, setPagination] = useState<Pagination>({
        page: cachedTableControls.page,
        size: cachedTableControls.size,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
        sorted: false
    });

    const [filteredSupportRequests, setFilteredSupportRequests] = useState<
        FastshiftSupportRequest[]
    >([]);

    useFetchSupportRequests();

    // Sync local pagination with cached controls and recalculate pagination metadata
    useEffect(() => {
        if (!supportRequestsData) {
            setPagination({
                page: DEFAULT_CONTROLS_PAYLOAD.page,
                size: DEFAULT_CONTROLS_PAYLOAD.size,
                totalElements: 0,
                totalPages: 0,
                hasNext: false,
                hasPrevious: false,
                sorted: false
            });
            return;
        }

        const page = cachedTableControls.page;
        const size = cachedTableControls.size;
        const totalElements = filteredSupportRequests.length;
        const totalPages = Math.ceil(totalElements / size);

        setPagination({
            page,
            size,
            totalElements,
            totalPages,
            hasNext: page < totalPages - 1,
            hasPrevious: page > 0,
            sorted: false
        });
    }, [
        cachedTableControls.page,
        cachedTableControls.size,
        supportRequestsData,
        filteredSupportRequests
    ]);

    // Recalculate filtered support requests with support requests data and cached table controls
    useEffect(() => {
        if (!supportRequestsData) {
            setFilteredSupportRequests([]);
            return;
        }

        const filtered = applyFilters(
            supportRequestsData,
            cachedTableControls?.filters
        );

        const searched = applySearch(
            filtered,
            cachedTableControls.searchTerm ?? ""
        );

        setFilteredSupportRequests(searched);
    }, [
        supportRequestsData,
        cachedTableControls.filters,
        cachedTableControls.searchTerm
    ]);

    const onSetControlsPayload = (controls: ControlsPayload) => {
        if (!supportRequestsData) return;

        // If filters or search term changed and we're not on page 0, reset to page 0
        const searchTermChanged =
            controls.searchTerm !== cachedTableControls.searchTerm;
        const filtersChanged = !isEqual(
            controls.filters ?? {},
            cachedTableControls.filters ?? {}
        );

        const updatedControls =
            (searchTermChanged || filtersChanged) && controls.page !== 0
                ? { ...controls, page: 0 }
                : controls;

        // Dispatch to Redux - useEffect will sync local state
        dispatch(
            StateActions.setTableControls({
                tableId: TABLE_IDS.MIGRATION_TICKETS,
                controls: updatedControls
            })
        );
    };

    const handleRefresh = () => {
        if (!customerDomainData || !selectedDomainOrg) return;

        const supportTicketQueryParams = getOrgQueryParam(selectedDomainOrg);

        dispatch(
            StateActions.getFastshiftSupportRequests({
                stargateClient,
                customerDomainId: customerDomainData.customerDomainId,
                queryParams: supportTicketQueryParams
            })
        );
    };

    const baseHead: TableHeads = useMemo(
        () => ({
            cells: [
                {
                    key: "ticket",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTableTicketColumnTitle}
                            />
                        </Heading>
                    )
                },
                {
                    key: "status",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTableStatusColumnTitle}
                            />
                        </Heading>
                    ),
                    width: 10
                },
                {
                    key: "phase",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTablePhaseColumnTitle}
                            />
                        </Heading>
                    ),
                    width: 11
                },
                {
                    key: "source",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTableSourceColumnTitle}
                            />
                        </Heading>
                    ),
                    width: 20
                },
                {
                    key: "destination",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTableDestinationColumnTitle}
                            />
                        </Heading>
                    ),
                    width: 20
                },
                {
                    key: "planToMigrateDate",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTablePlanToMigrateDateColumnTitle}
                            />
                        </Heading>
                    ),
                    width: 13
                }
            ]
        }),
        []
    );

    const head: TableHeads = useMemo(() => {
        if (!canViewRunbookActions) {
            return baseHead;
        }

        return {
            cells: [
                ...baseHead.cells,
                {
                    key: "actions",
                    content: (
                        <Heading size="xxsmall">
                            <FormattedMessage
                                {...messages.migrationTicketsTableActionsColumnTitle}
                            />
                        </Heading>
                    ),
                    isSortable: false,
                    width: 6
                }
            ]
        };
    }, [baseHead, canViewRunbookActions]);

    const paginatedSupportRequests = useMemo(
        () =>
            (filteredSupportRequests ?? []).slice(
                pagination.page * pagination.size,
                (pagination.page + 1) * pagination.size
            ),
        [filteredSupportRequests, pagination.page, pagination.size]
    );

    const rows: TableRows = useMemo(() => {
        if (isRunbooksExperienceEnabled) {
            const colSpan = head.cells.length;

            return paginatedSupportRequests.flatMap((request) => {
                const isExpanded = expandedTicketIds.has(
                    request.supportTicketId
                );
                const statusMessage = getStatusText(
                    request.fastShiftStatusReason
                );
                const statusText = intl.formatMessage(statusMessage);

                const cells = [
                    {
                        key: "ticket",
                        content: (
                            <Inline space="space.100" alignBlock="center">
                                <IconButton
                                    spacing="compact"
                                    label={intl.formatMessage(
                                        messages.migrationTicketsTableViewRelatedTicketsTooltip
                                    )}
                                    icon={
                                        isExpanded
                                            ? ChevronDownIcon
                                            : ChevronRightIcon
                                    }
                                    isTooltipDisabled={false}
                                    onClick={() =>
                                        toggleExpandedTicket(
                                            request.supportTicketId
                                        )
                                    }
                                    testId={`migration-tickets-expand-${request.supportTicketId}`}
                                />
                                <Stack xcss={ticketCellStyles}>
                                    <Tooltip content={request.title}>
                                        <Box xcss={textEllipsisStyles}>
                                            <Text>{request.title}</Text>
                                        </Box>
                                    </Tooltip>
                                    <Text color="color.text.selected">
                                        <Link
                                            href={`${SAC_URL}/requests/${request.supportTicketId}`}
                                            target="_blank"
                                            onClick={() => {
                                                sendMigrationTicketsTableOpenSupportTicketClickUIEvent(
                                                    request.supportTicketId
                                                );
                                            }}
                                        >
                                            <Text color="inherit">
                                                {request.supportTicketId}
                                            </Text>
                                        </Link>
                                        {request.relatedTicketsCount > 0 && (
                                            <Box
                                                as="span"
                                                xcss={ticketBadgeStyles}
                                            >
                                                <Badge>
                                                    +
                                                    {
                                                        request.relatedTicketsCount
                                                    }
                                                </Badge>
                                            </Box>
                                        )}
                                    </Text>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "status",
                        content: (
                            <Box as="span">
                                <Tooltip content={statusText}>
                                    <Lozenge
                                        appearance={getStatusColor(
                                            request.fastShiftStatusReason
                                        )}
                                    >
                                        {statusText}
                                    </Lozenge>
                                </Tooltip>
                            </Box>
                        )
                    },
                    {
                        key: "phase",
                        content: (
                            <Text>
                                <FormattedMessage
                                    {...getMigrationPhaseDisplayText(
                                        request.migrationPhase
                                    )}
                                />
                            </Text>
                        )
                    },
                    {
                        key: "source",
                        content: (
                            <Inline space="space.100" alignBlock="center">
                                <ProductIcon
                                    productName={
                                        request.source
                                            .productName as ProductName
                                    }
                                    variant={PRODUCT_VARIANTS.SOURCE}
                                />
                                <Stack xcss={stackInlineStyles}>
                                    <Text>
                                        {request.source.productName ? (
                                            productNameMapping[
                                                request.source.productName
                                            ]
                                        ) : (
                                            <FormattedMessage
                                                {...messages.migrationTicketsTableSourceDestinationColumnContentDefault}
                                            />
                                        )}
                                    </Text>
                                    <Tooltip
                                        content={
                                            request.source.baseUrl ||
                                            intl.formatMessage(
                                                messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl
                                            )
                                        }
                                    >
                                        <Box xcss={smallTextEllipsisStyles}>
                                            {request.source.baseUrl || (
                                                <FormattedMessage
                                                    {...messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "destination",
                        content: (
                            <Inline space="space.100" alignBlock="center">
                                <ProductIcon
                                    productName={
                                        request.destination
                                            .productName as ProductName
                                    }
                                    variant={PRODUCT_VARIANTS.DESTINATION}
                                />
                                <Stack xcss={stackInlineStyles}>
                                    <Text>
                                        {request.destination.productName ? (
                                            productNameMapping[
                                                request.destination.productName
                                            ]
                                        ) : (
                                            <FormattedMessage
                                                {...messages.migrationTicketsTableSourceDestinationColumnContentDefault}
                                            />
                                        )}
                                    </Text>
                                    <Tooltip
                                        content={
                                            request.destination.baseUrl ||
                                            intl.formatMessage(
                                                messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl
                                            )
                                        }
                                    >
                                        <Box xcss={smallTextEllipsisStyles}>
                                            {request.destination.baseUrl || (
                                                <FormattedMessage
                                                    {...messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "planToMigrateDate",
                        content: (
                            <Text>
                                {request.planToMigrateDate ? (
                                    <FormattedDate
                                        value={
                                            new Date(request.planToMigrateDate)
                                        }
                                        day="2-digit"
                                        month="short"
                                        year="numeric"
                                    />
                                ) : (
                                    <FormattedMessage
                                        {...messages.migrationTicketsTableNoDatePlanned}
                                    />
                                )}
                            </Text>
                        )
                    },
                    ...(canViewRunbookActions
                        ? [
                              {
                                  key: "actions",
                                  content: (
                                      <IconButton
                                          spacing="compact"
                                          label={intl.formatMessage(
                                              messages.migrationTicketsTableViewRunbooksTooltip
                                          )}
                                          icon={BookWithBookmarkIcon}
                                          isTooltipDisabled={false}
                                          onClick={() =>
                                              openPanelWithRunbooks(request)
                                          }
                                          testId={`migration-tickets-runbooks-${request.supportTicketId}`}
                                      />
                                  )
                              }
                          ]
                        : [])
                ];

                const baseRow = {
                    key: request.supportTicketId,
                    cells
                };

                if (!isExpanded) {
                    return [baseRow];
                }

                return [
                    baseRow,
                    {
                        key: `${request.supportTicketId}-related-tickets`,
                        cells: [
                            {
                                key: "related-tickets",
                                colSpan,
                                content: (
                                    <RelatedTicketsExpandedRow
                                        relatedTickets={request.relatedTickets}
                                        onClickAnalyticsEvent={
                                            sendRelatedTicketsSidePanelOpenSupportTicketClickUIEvent
                                        }
                                    />
                                )
                            }
                        ]
                    }
                ];
            });
        }

        return paginatedSupportRequests.map((request) => {
            const statusMessage = getStatusText(request.fastShiftStatusReason);
            const statusText = intl.formatMessage(statusMessage);

            return {
                key: request.supportTicketId,
                onClick: () => {
                    openPanelWithRelatedTickets(request);
                    sendRelatedTicketsSidePanelLinkClickUIEvent(
                        request.supportTicketId
                    );
                },
                onKeyPress: (event: KeyboardEvent) => {
                    if (event.key === "Enter") {
                        openPanelWithRelatedTickets(request);
                        sendRelatedTicketsSidePanelLinkClickUIEvent(
                            request.supportTicketId
                        );
                    }
                },
                tabIndex: 0,
                cells: [
                    {
                        key: "ticket",
                        content: (
                            <Stack>
                                <Tooltip content={request.title}>
                                    <Box xcss={textEllipsisStyles}>
                                        <Text>{request.title}</Text>
                                    </Box>
                                </Tooltip>
                                <Text color="color.text.selected">
                                    <Link
                                        href={`${SAC_URL}/requests/${request.supportTicketId}`}
                                        target="_blank"
                                        onClick={(
                                            e: React.MouseEvent<HTMLAnchorElement>
                                        ) => {
                                            e.stopPropagation();
                                            sendMigrationTicketsTableOpenSupportTicketClickUIEvent(
                                                request.supportTicketId
                                            );
                                        }}
                                    >
                                        <Text color="inherit">
                                            {request.supportTicketId}
                                        </Text>
                                    </Link>
                                    {request.relatedTicketsCount > 0 && (
                                        <Box as="span" xcss={ticketBadgeStyles}>
                                            <Badge>
                                                +{request.relatedTicketsCount}
                                            </Badge>
                                        </Box>
                                    )}
                                </Text>
                            </Stack>
                        )
                    },
                    {
                        key: "status",
                        content: (
                            <Box as="span">
                                <Tooltip content={statusText}>
                                    <Lozenge
                                        appearance={getStatusColor(
                                            request.fastShiftStatusReason
                                        )}
                                    >
                                        {statusText}
                                    </Lozenge>
                                </Tooltip>
                            </Box>
                        )
                    },
                    {
                        key: "phase",
                        content: (
                            <Text>
                                <FormattedMessage
                                    {...getMigrationPhaseDisplayText(
                                        request.migrationPhase
                                    )}
                                />
                            </Text>
                        )
                    },
                    {
                        key: "source",
                        content: (
                            <Inline space="space.100" alignBlock="center">
                                <ProductIcon
                                    productName={
                                        request.source
                                            .productName as ProductName
                                    }
                                    variant={PRODUCT_VARIANTS.SOURCE}
                                />
                                <Stack xcss={stackInlineStyles}>
                                    <Text>
                                        {request.source.productName ? (
                                            productNameMapping[
                                                request.source.productName
                                            ]
                                        ) : (
                                            <FormattedMessage
                                                {...messages.migrationTicketsTableSourceDestinationColumnContentDefault}
                                            />
                                        )}
                                    </Text>
                                    <Tooltip
                                        content={
                                            request.source.baseUrl ||
                                            intl.formatMessage(
                                                messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl
                                            )
                                        }
                                    >
                                        <Box xcss={smallTextEllipsisStyles}>
                                            {request.source.baseUrl || (
                                                <FormattedMessage
                                                    {...messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "destination",
                        content: (
                            <Inline space="space.100" alignBlock="center">
                                <ProductIcon
                                    productName={
                                        request.destination
                                            .productName as ProductName
                                    }
                                    variant={PRODUCT_VARIANTS.DESTINATION}
                                />
                                <Stack xcss={stackInlineStyles}>
                                    <Text>
                                        {request.destination.productName ? (
                                            productNameMapping[
                                                request.destination.productName
                                            ]
                                        ) : (
                                            <FormattedMessage
                                                {...messages.migrationTicketsTableSourceDestinationColumnContentDefault}
                                            />
                                        )}
                                    </Text>
                                    <Tooltip
                                        content={
                                            request.destination.baseUrl ||
                                            intl.formatMessage(
                                                messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl
                                            )
                                        }
                                    >
                                        <Box xcss={smallTextEllipsisStyles}>
                                            {request.destination.baseUrl || (
                                                <FormattedMessage
                                                    {...messages.migrationTicketsTableSourceDestinationColumnContentNoBaseUrl}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                </Stack>
                            </Inline>
                        )
                    },
                    {
                        key: "planToMigrateDate",
                        content: (
                            <Text>
                                {request.planToMigrateDate ? (
                                    <FormattedDate
                                        value={
                                            new Date(request.planToMigrateDate)
                                        }
                                        day="2-digit"
                                        month="short"
                                        year="numeric"
                                    />
                                ) : (
                                    <FormattedMessage
                                        {...messages.migrationTicketsTableNoDatePlanned}
                                    />
                                )}
                            </Text>
                        )
                    }
                ]
            };
        });
    }, [
        expandedTicketIds,
        head.cells.length,
        intl,
        canViewRunbookActions,
        isRunbooksExperienceEnabled,
        openPanelWithRelatedTickets,
        openPanelWithRunbooks,
        paginatedSupportRequests,
        sendMigrationTicketsTableOpenSupportTicketClickUIEvent,
        sendRelatedTicketsSidePanelLinkClickUIEvent,
        sendRelatedTicketsSidePanelOpenSupportTicketClickUIEvent,
        toggleExpandedTicket
    ]);

    return {
        isTableLoading: customerDomainPending || supportRequestsPending,
        isTableError: !!supportRequestsError,
        head,
        rows,
        handleRefresh,
        pagination,
        onSetControlsPayload,
        controls: cachedTableControls,
        filters: cachedTableControls?.filters,
        enableRowClick: !isRunbooksExperienceEnabled
    };
};
